import React, { useEffect, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import type { OpsEnvironment, SecretsConfig } from './opsConsoleData';
import { loadSecrets, resetSecrets, saveSecrets } from './opsConsoleStorage';
import { PanelCard, PrimaryButton, TextInput } from './components';

export default function SecretsPanel({ env }: { env: OpsEnvironment }) {
  const [secrets, setSecrets] = useState<SecretsConfig>(() => loadSecrets(env));
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    setSecrets(loadSecrets(env));
    setSavedAt(null);
  }, [env]);

  const update = <K extends keyof SecretsConfig>(key: K, value: SecretsConfig[K]) => {
    setSecrets((prev) => ({ ...prev, [key]: value }));
    setSavedAt(null);
  };

  const onSave = () => {
    saveSecrets(env, secrets);
    setSavedAt(new Date().toLocaleTimeString());
  };

  const onReset = () => {
    setSecrets(resetSecrets(env));
    setSavedAt(null);
  };

  return (
    <PanelCard
      title="Secrets manager"
      subtitle={`Synthetic connection defaults for ${env} — stored in localStorage only`}
      action={
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[10px] text-emerald-600">Saved {savedAt}</span>}
          <PrimaryButton variant="secondary" onClick={onReset}>
            <RotateCcw size={12} /> Reset
          </PrimaryButton>
          <PrimaryButton onClick={onSave}>
            <Save size={12} /> Save
          </PrimaryButton>
        </div>
      }
    >
      <div className="space-y-4">
        <section className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Default PostgreSQL
          </h4>
          <TextInput
            label="Connection string"
            value={secrets.postgres}
            onChange={(v) => update('postgres', v)}
            mono
            secret
          />
        </section>

        <section className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Redis</h4>
          <TextInput label="URL" value={secrets.redis} onChange={(v) => update('redis', v)} mono secret />
        </section>

        <section className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Mongo</h4>
          <TextInput
            label="Connection string"
            value={secrets.mongo}
            onChange={(v) => update('mongo', v)}
            mono
            secret
          />
        </section>

        <section className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Temporal</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextInput
              label="Host"
              value={secrets.temporalHost}
              onChange={(v) => update('temporalHost', v)}
              mono
            />
            <TextInput
              label="Namespace"
              value={secrets.temporalNamespace}
              onChange={(v) => update('temporalNamespace', v)}
              mono
            />
          </div>
        </section>

        <section className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">RabbitMQ</h4>
          <TextInput
            label="URL"
            value={secrets.rabbitmq}
            onChange={(v) => update('rabbitmq', v)}
            mono
            secret
          />
        </section>

        <section className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Logs connection
          </h4>
          <TextInput
            label="OpenSearch / Elastic URL"
            value={secrets.logs}
            onChange={(v) => update('logs', v)}
            mono
            secret
          />
        </section>
      </div>
    </PanelCard>
  );
}
